import {Injectable} from '@angular/core';
import {EventService} from './event.service';
import {map, switchMap, take, tap} from 'rxjs/operators';
import {UiModelService} from './ui-model.service';
import {HttpClient} from '@angular/common/http';
import {UiControl} from '../models/uiControl';
import {
  flatMap,
  ListControl,
  ListItemControl,
  UIArtefakt,
  UiBaseControl
} from '../models/uiArtefakt';
import {Observable, Subscription} from 'rxjs';
import {v4 as uuid} from 'uuid';

const products_with_testdata = [
  {
    produktId: 'SBU',
    testdata: [
      'DefaultTest',
      'PoolPartnerTest',
      'BoniTest',
      'ComplianceTest'
    ]
  }, {
    produktId: 'SBUDirektverkauf',
    testdata: [
      'DefaultTest'
    ]
  }, {
    produktId: 'SBUg',
    testdata: [
      'DefaultTest'
    ]
  }, {
    produktId: 'SBUV',
    testdata: [
      'DefaultTest'
    ]
  }, {
    produktId: 'SBUKollektivSiemens',
    testdata: [
      'DefaultTest'
    ]
  }, {
    produktId: 'SBUDirektversicherung',
    testdata: [
      'DefaultTest'
    ]
  }, {
    produktId: 'SBURueckdeckungsversicherung',
    testdata: [
      'DefaultTest'
    ]
  }, {
    produktId: 'FRV',
    testdata: [
      'DefaultTest',
      'IndividuelleFondsMitZuzahlung',
      'JuristischePersonMitGWG',
      'MitgliederVertretung',
      'NatuerlichePersonWirtschaftlichBerechtigter',
    ]
  }, {
    produktId: 'FRVKids',
    testdata: [
      'DefaultTest'
    ]
  }, {
    produktId: 'SBUgDirektversicherungKollektivFeneberg',
    testdata: [
      'DefaultTest'
    ]
  }, {
    produktId: 'FBRV',
    testdata: [
      'DefaultTest'
    ]
  }, {
    produktId: 'FRVDirektversicherung',
    testdata: [
      'DefaultTest'
    ]
  }, {
    produktId: 'FRVRueckdeckungsversicherungPensionszusage',
    testdata: [
      'DefaultTest'
    ]
  }, {
    produktId: 'FRVRueckdeckungsversicherungUnterstuetzungskasseIntern',
    testdata: [
      'DefaultTest',
      '15152',
      '12203',
    ]
  }, {
    produktId: 'FRVRueckdeckungsversicherungUnterstuetzungskasseExtern',
    testdata: [
      'DefaultTest',
      '12155',
    ]
  }
];

function uniqueDataKey(artefakt: UIArtefakt): string {
  let key = artefakt.Identifikator;
  if (!!(artefakt as UiBaseControl).ReferenzIdentifikator) {
    const refId = (artefakt as UiBaseControl).ReferenzIdentifikator;
    key = key + refId.substring(0, refId.indexOf('@'));
  }
  return key;
}

function maxVirtualId(artefakte: UIArtefakt[]): number {
  return flatMap(artefakte)
  .filter(artefakt => artefakt.UIArtefakttyp === 'UIVorgangReferenz')
  .map(artefakt => artefakt.WertEingabe)
  .reduce((previousValue: number, currentValue: number) => {
    if (previousValue > currentValue || currentValue > 99) {
      return previousValue;
    }
    return currentValue;
  }, 1);
}

@Injectable()
export class AutofillService {
  private _subscription: Subscription = Subscription.EMPTY;
  private _virtualCounter = 0;

  constructor(private _eventService: EventService, private _uiModelService: UiModelService,
              private _http: HttpClient) {
  }

  public getTestData(): Observable<string[]> {
    return this._eventService.uiControls$.pipe(take(1),
        map(control => {
          let produktId = control.produktId;
          if (!products_with_testdata.filter(produkt => produkt.produktId === produktId).length) {
            produktId = 'SBU';
          }
          return products_with_testdata.filter(produkt => produkt.produktId === produktId)[0].testdata;
        }));
  }

  public autofill(testdata: string): Observable<boolean> {
    return this._eventService.uiControls$.pipe(
      switchMap(uiControl => {
        let produktId = uiControl.produktId;
        if (!products_with_testdata.filter(produkt => produkt.produktId === produktId).length) {
          produktId = 'SBU';
        }
        return this._http.get<UiControl>(`assets/testdata/${produktId}/${testdata}.json`).pipe(
          tap(control => {
            this._virtualCounter = maxVirtualId(uiControl.uiVorgang.UIArtefakt) + 1;
            this.mapData(control.uiVorgang.UIArtefakt, uiControl.uiVorgang.UIArtefakt);
          }),
          switchMap(_ => this._uiModelService.requestUiControl(uiControl)),
        );
      }),
      take(2));
  }

  private mapData(testData: UIArtefakt[], runtimeData: UIArtefakt[]) {
    const dataMap: Map<string, any> = new Map<string, any>();
    let flatArtefakts = this.flatMapWithoutListe(testData);
    flatArtefakts.forEach(entry => {
      if (entry.UIArtefakttyp === 'Liste') {
        dataMap.set(uniqueDataKey(entry), entry.UIArtefakt);
      } else if ((!!entry.WertEingabe || entry.WertEingabe === false) && entry.UIArtefakttyp !== 'UIVorgangReferenz') {
        dataMap.set(uniqueDataKey(entry), entry.WertEingabe);
      }
    });

    flatArtefakts = this.flatMapWithoutListe(runtimeData);
    flatArtefakts.forEach(uiArtefakt => {
      if (dataMap.has(uniqueDataKey(uiArtefakt))) {
        if (uiArtefakt.UIArtefakttyp === 'Liste') {
          this.autofillListe(uiArtefakt as ListControl, dataMap.get(uniqueDataKey(uiArtefakt)));
        } else {
          if (uiArtefakt.Identifikator === 'UIArtefaktTextNachnameVersichertePerson') {
            uiArtefakt.WertEingabe = this.createUniqueNachname(dataMap.get(uniqueDataKey(uiArtefakt)));
            uiArtefakt.recreateComponent = true;
          } else {
            uiArtefakt.WertEingabe = dataMap.get(uniqueDataKey(uiArtefakt));
            uiArtefakt.recreateComponent = true;
          }
        }
      }
    });
  }

  private autofillListe(list: ListControl, entries: UIArtefakt[]) {
    if (list.ListeErweiterbarJN) {
      let template = list.template;
      if (!template) {
        template = list.UIArtefakt.filter(item => (item as ListItemControl).TemplateJN)[0] as ListItemControl;
      }
      if (!template) {
        template = list.UIArtefakt[0] as ListItemControl;
      }
      list.UIArtefakt = entries.map(entry => {
        const newEntry = JSON.parse(JSON.stringify(template)) as ListItemControl;
        this.mapData(entry.UIArtefakt, newEntry.UIArtefakt);
        this.injectVirtualIds(newEntry.UIArtefakt);
        newEntry.TemplateJN = (entry as ListItemControl).TemplateJN;
        newEntry.UniqueTrackingID = uuid();
        return newEntry;
      });
    }
  }

  private flatMapWithoutListe(array: UIArtefakt[]): UIArtefakt[] {
    return array.reduce((previousValue: UIArtefakt[], currentValue: UIArtefakt) => {
          previousValue.push(currentValue);
          if (!!currentValue.UIArtefakt && currentValue.UIArtefakttyp !== 'Liste') {
            previousValue.push(...flatMap(currentValue.UIArtefakt));
          }
          return previousValue;
        },
        [] as UIArtefakt[]);
  }

  private injectVirtualIds(artefakte: UIArtefakt[]) {
    const uiRefs = artefakte.filter(artefakt => artefakt.UIArtefakttyp === 'UIVorgangReferenz');
    uiRefs.forEach(ref => {
      artefakte.filter(artefakt => (artefakt as UiBaseControl).ReferenzIdentifikator === ref.IdentifikatorUuid + '@' + ref.WertEingabe)
      .forEach(artefakt => {
        (artefakt as UiBaseControl).ReferenzIdentifikator = ref.IdentifikatorUuid + '@' + this._virtualCounter;
      });
      ref.WertEingabe = this._virtualCounter;
      this._virtualCounter++;
    });
    artefakte.filter(artefakt => !!artefakt.UIArtefakt).forEach(artefakt => this.injectVirtualIds(artefakt.UIArtefakt));
  }

  private createUniqueNachname(name: string): string {
    let uname = name + '-' + this.createSimpleTimestamp();
    if (uname.length > 30) {
      uname = uname.slice(0, 30);
    }
    return uname;
  }

  private createSimpleTimestamp(): string {
    const date = new Date();
    return date.getFullYear().toString().slice(-2)
        + ('0' + (date.getMonth() + 1)).slice(-2)
        + ('0' + date.getDate()).slice(-2)
        + ('0' + date.getHours() ).slice(-2)
        + ('0' + date.getMinutes()).slice(-2)
        + ('0' + date.getSeconds()).slice(-2);
  }
}
